import React, { useState, memo } from 'react';
import {
  AreaIndicator
} from '../../components/job-indicators';
import { formatJobDate, formatTime, getCheckoutTime, getBorderColor, isManualJob } from '../../utils/job-utils';
import JobBadges from '../schedule/indicators/JobBadges';
import NextCheckInDisplay from '../../components/NextCheckInDisplay';
import DueByDisplay from '../../components/DueByDisplay';

// Helper function to display user role properly
const getDisplayRole = (employee) => {
  // Handle team assignments
  if (employee.role === 'TEAM') {
    return 'Team Assignment';
  }
  
  // First check if there's a department field, use that if available
  if (employee.department) {
    return employee.department;
  }
  
  // Normalize the role to handle case sensitivity and trim whitespace
  const normalizedRole = employee.role ? employee.role.toString().trim().toUpperCase() : '';
  
  // Otherwise, map the role to a user-friendly display name
  switch (normalizedRole) {
    case 'ADMIN':
      return 'Admin';
    case 'HOUSEKEEPER':
      return 'Housekeeper';
    case 'HOUSEKEEPER_MANAGER':
      return 'Housekeeping Manager';
    case 'INSPECTOR':
      return 'Inspector';
    case 'PROPERTY_MANAGER':
      return 'Property Manager';
    case 'MAINTENANCE':
      return 'Maintenance';
    case 'USER':
      return 'User';
    default:
      return employee.role || 'Staff';
  }
};

const AssignedTasks = memo(function AssignedTasks({ groupedAssignedJobs, totalJobsCount, setEditingJob }) {
  // Default to expanded
  const [isExpanded, setIsExpanded] = useState(true);
  // Track expanded state for each employee
  const [expandedEmployees, setExpandedEmployees] = useState({});
  // State to control all employee tabs at once
  const [allEmployeesExpanded, setAllEmployeesExpanded] = useState(true);

  // Helper function to get checkout date for display
  // Only shows date for CHECKIN-based jobs (like pre-arrival inspections)
  // because the previous checkout date might be different from the job's scheduled date
  const getCheckoutDate = (job) => {
    // Only show date for CHECKIN-based jobs
    if (job.serviceType?.timeBase !== 'CHECKIN') {
      return null;
    }
    
    // For CHECKIN-based jobs, use previous reservation checkout
    if (job.previousReservation?.check_out) {
      try {
        const checkoutDate = new Date(job.previousReservation.check_out);
        const month = checkoutDate.getMonth() + 1;
        const day = checkoutDate.getDate();
        return `${month}/${day}`;
      } catch (error) {
        return null;
      }
    }
    
    return null;
  };

  const toggleAccordion = (e) => {
    // Stop event propagation to prevent it from affecting other components
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  // Toggle individual employee accordion
  const toggleEmployeeAccordion = (e, employeeId) => {
    e.stopPropagation();
    setExpandedEmployees(prev => ({
      ...prev,
      [employeeId]: !prev[employeeId]
    }));
  };

  // Toggle all employee tabs at once
  const toggleAllEmployeeTabs = (e) => {
    e.stopPropagation();
    const newState = !allEmployeesExpanded;
    setAllEmployeesExpanded(newState);
    
    // Update all employee expanded states
    const employeeIds = groupedAssignedJobs.map(item => item.employee.id);
    const newExpandedState = {};
    employeeIds.forEach(id => {
      newExpandedState[id] = newState;
    });
    setExpandedEmployees(newExpandedState);
  };

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div 
        className="p-4 border-b flex items-center justify-between cursor-pointer hover:bg-gray-50" 
        onClick={toggleAccordion}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <h2 className="text-xl font-semibold whitespace-nowrap">Assigned Tasks</h2>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Small button to collapse/expand all employee tabs */}
          {isExpanded && groupedAssignedJobs.length > 0 && (
            <button
              onClick={toggleAllEmployeeTabs}
              className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded border border-gray-300 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 whitespace-nowrap"
              title={allEmployeesExpanded ? "Collapse all employee tabs" : "Expand all employee tabs"}
            >
              {allEmployeesExpanded ? (
                <div className="flex items-center gap-1">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                  </svg>
                  <span className="hidden sm:inline">Collapse All</span>
                  <span className="sm:hidden">Collapse</span>
                </div>
              ) : (
                <div className="flex items-center gap-1">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                  <span className="hidden sm:inline">Expand All</span>
                  <span className="sm:hidden">Expand</span>
                </div>
              )}
            </button>
          )}
          <div className="text-gray-500">
            {isExpanded ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            )}
          </div>
        </div>
      </div>
      
      {isExpanded && (
        <div className="space-y-4 p-4">
          {groupedAssignedJobs.map(({ employee, jobs, isTeamAssignment, teamId }) => {
            // Use the centrally controlled state, defaulting to expanded if not set
            const isEmployeeExpanded = expandedEmployees[employee.id] !== undefined 
              ? expandedEmployees[employee.id] 
              : allEmployeesExpanded;
            
            return (
              <div key={employee.id} className={`${isTeamAssignment ? 'bg-blue-50 border-2 border-blue-200' : 'bg-white'} rounded-lg shadow overflow-hidden`}>
                <div 
                  className={`p-4 ${isTeamAssignment ? 'bg-blue-100' : 'bg-gray-50'} border-b flex items-center justify-between cursor-pointer hover:${isTeamAssignment ? 'bg-blue-200' : 'bg-gray-100'}`}
                  onClick={(e) => toggleEmployeeAccordion(e, employee.id)}
                >
                  <div className="flex items-center gap-2">
                    {isTeamAssignment && (
                      <svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    )}
                    <div className="flex flex-col">
                      <h3 className="font-medium">{employee.name}</h3>
                      {isTeamAssignment && (
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-blue-600 font-medium">
                            {employee.memberCount} member{employee.memberCount !== 1 ? 's' : ''}
                          </span>
                        </div>
                      )}
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      isTeamAssignment 
                        ? 'bg-blue-200 text-blue-800' 
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {getDisplayRole(employee)}
                    </span>
                    <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                      {jobs.length} {jobs.length === 1 ? 'job' : 'jobs'}
                    </span>
                  </div>
                  <div className="text-gray-500">
                    {isEmployeeExpanded ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    )}
                  </div>
                </div>
                
                {isEmployeeExpanded && (
                  <div className="space-y-2 overflow-y-auto pb-2">
                    {jobs.map((job, index) => {
                      const isFlipJob = job.isFlip;
                      const shouldUseServiceTypeColor = job.serviceType?.color;
                      
                      // Service type color style for direct CSS if needed
                      const serviceTypeColorStyle = shouldUseServiceTypeColor 
                        ? { 
                            '--service-type-color': isFlipJob ? '#ef4444' : job.serviceType.color, // Red for flip jobs
                            borderLeftColor: isFlipJob ? '#ef4444' : job.serviceType.color // Red for flip jobs
                          } 
                        : {};

                      // For assigned tasks, flip jobs get red left border, others use service type color
                      const finalBorderClass = shouldUseServiceTypeColor
                        ? isFlipJob 
                          ? 'border-l-[24px] relative border-2 border-red-500'  // Red left border + red outer border (2px) for flip
                          : 'border-l-[24px] relative border border-gray-200'   // Service type color + gray border
                        : isFlipJob
                          ? 'border-l-[24px] border-2 border-red-500'           // Gray left border + red outer border (2px) for flip
                          : 'border-l-[24px] border border-gray-200';           // Gray left border + gray border
                      
                      return (
                        <div 
                          key={job.id} 
                          className={`bg-gray-50 rounded-lg shadow ${finalBorderClass} cursor-pointer relative mx-2`}
                          style={serviceTypeColorStyle}
                        >
                          <div className="flex">
                            <div className="flex-1 p-2 lg:p-4">
                              <div className="grid grid-cols-[3fr_2fr] gap-4">
                                {/* Left Column - 3/5 width */}
                                <div>
                                  <div className="flex items-center gap-2 mb-1">
                                    <h4 className="font-medium">{job.property.name}</h4>

                                  </div>
                                  <p className="text-xs text-gray-500 mb-1">{formatJobDate(job.scheduledDate)}</p>
                                  {/* Show Guest Checkout for non-custom jobs */}
                                  {!isManualJob(job) && job.serviceType?.timeBase !== 'CUSTOM' && (
                                    <div className="flex items-center gap-2 mb-1">
                                      <div className="flex items-center">
                                        <div className="text-sm mr-1 font-medium text-gray-600">
                                          Guest Checkout:
                                        </div>
                                        {job.hasTimeOverride ? (
                                          <div className="flex flex-col text-sm">
                                            {getCheckoutTime(job) && (
                                              <span className="line-through text-gray-400">
                                                {formatTime(getCheckoutTime(job))}
                                                {getCheckoutDate(job) && <span className="ml-1">on {getCheckoutDate(job)}</span>}
                                              </span>
                                            )}
                                            <span className="flex items-center text-sky-600 font-semibold">
                                              <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 mr-0.5" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                                              </svg>
                                              {formatTime(job.startTime)}
                                              {getCheckoutDate(job) && <span className="ml-1">on {getCheckoutDate(job)}</span>}
                                            </span>
                                          </div>
                                        ) : (
                                          <div className="text-sm text-gray-800 font-semibold">
                                            {formatTime(getCheckoutTime(job))}
                                            {getCheckoutDate(job) && <span className="text-gray-600 ml-1">on {getCheckoutDate(job)}</span>}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  {/* Show Start Time for custom/manual jobs */}
                                  {(isManualJob(job) || job.serviceType?.timeBase === 'CUSTOM') && job.startTime && (
                                    <div className="flex items-center gap-2 mb-1">
                                      <div className="flex items-center">
                                        <div className="text-sm mr-1 font-medium text-gray-600">
                                          Start Time:
                                        </div>
                                        <div className="text-sm text-gray-800 font-semibold">{formatTime(job.startTime)}</div>
                                      </div>
                                    </div>
                                  )}
                                  <div className="text-sm mt-1 bg-blue-50 p-1 rounded">
                                    <span className="inline-flex items-center">
                                      <svg className="h-4 w-4 mr-1 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                      </svg>
                                      <span className="text-gray-600 font-medium mr-1">Due by:</span>
                                      <span className="text-blue-700 font-semibold"><DueByDisplay job={job} /></span>
                                    </span>
                                  </div>
                                  {job.property.area && (
                                    <div className="mt-2">
                                      <AreaIndicator area={job.property.area} />
                                    </div>
                                  )}
                                </div>
                                
                                {/* Right Column - 2/5 width */}
                                <div className="flex flex-col justify-between">
                                  {/* Badges at top */}
                                  <div className="flex flex-wrap flex-row-reverse gap-1 justify-start mb-2">
                                    {/* Use JobBadges component to ensure early check-in detection works for cleaning jobs */}
                                    <JobBadges job={job} allJobs={jobs} />
                                  </div>
                                  
                                  {/* Edit button at bottom */}
                                  <div className="flex justify-end">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation(); // Prevent triggering accordion toggle
                                        setEditingJob(job);
                                      }}
                                      className="text-gray-400 hover:text-gray-600"
                                    >
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-5 w-5"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                                        />
                                      </svg>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          
          {groupedAssignedJobs.length === 0 && (
            <div className="p-6 text-center text-gray-500">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <p>No assigned tasks found</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

export default AssignedTasks; 